import React, { useState, useEffect, useMemo } from 'react';
import { fetchConstructionData } from '../../utils/api';
import { PlusIcon } from '@heroicons/react/24/outline';
import NewScopeModal from '../NewScopeModal';
import SearchBar from './filters/SearchBar';
import FilterChips from './filters/FilterChips';
import FilterPanel from './filters/FilterPanel';
import BudgetItemsTable from './table/BudgetItemsTable';
import LoadingSkeleton from './LoadingSkeleton';
import { groupScopesByMonth } from '../../utils/budgetUtils';

const BudgetItems = ({ onSelectScope }) => {
  const [scopes, setScopes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: 'scope', direction: 'ascending' });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showMonthHeaders, setShowMonthHeaders] = useState(true);
  const [showFilterOptions, setShowFilterOptions] = useState(false);
  
  // Filters
  const [filters, setFilters] = useState({
    status: [],
    budgetWarnings: [],
    searchText: '',
    minBudget: '',
    maxBudget: '',
    minCompletion: '',
    maxCompletion: ''
  });
  
  // Available status options
  const statusOptions = useMemo(() => {
    const statuses = new Set();
    scopes.forEach(scope => {
      if (scope.status) statuses.add(scope.status);
    });
    return Array.from(statuses).sort();
  }, [scopes]);
  
  // Available budget warning options
  const budgetWarningOptions = [
    { id: 'outOfBudget', label: 'Out of Budget' },
    { id: 'contractsHigher', label: 'Contracts > Budget' },
    { id: 'paymentsHigher', label: 'Payments > Contracts' },
    { id: 'noContracts', label: 'No Contracts' }
  ];
  
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const data = await fetchConstructionData();
        setScopes(data.scope_items || []);
        
        // Get all unique statuses
        const allStatuses = Array.from(new Set(data.scope_items.map(item => item.status).filter(Boolean)));
        
        // Initialize filters with all statuses checked
        setFilters(prev => ({
          ...prev,
          status: allStatuses
        }));
        
        setLoading(false);
      } catch (err) {
        setError('Failed to load construction data');
        setLoading(false);
        console.error(err);
      }
    };
    
    loadData();
  }, []);
  
  // Handle sorting
  const requestSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };
  
  // Toggle filter by status
  const toggleStatusFilter = (status) => {
    setFilters(prev => {
      const newStatuses = prev.status.includes(status)
        ? prev.status.filter(s => s !== status)
        : [...prev.status, status];
      return { ...prev, status: newStatuses };
    });
  };
  
  // Set status filter to only show the selected status
  const setStatusFilterOnly = (status) => {
    setFilters(prev => {
      return { ...prev, status: [status] };
    });
  };
  
  // Toggle filter by budget warning
  const toggleBudgetWarningFilter = (warningId) => {
    setFilters(prev => {
      const newWarnings = prev.budgetWarnings.includes(warningId)
        ? prev.budgetWarnings.filter(w => w !== warningId)
        : [...prev.budgetWarnings, warningId];
      return { ...prev, budgetWarnings: newWarnings };
    });
  };
  
  // Set budget warning filter to only show the selected warning
  const setBudgetWarningFilterOnly = (warningId) => {
    setFilters(prev => {
      return { ...prev, budgetWarnings: [warningId] };
    });
  };
  
  // Update text filter
  const updateSearchText = (e) => {
    setFilters(prev => ({ ...prev, searchText: e.target.value }));
  };
  
  // Update budget range filters
  const updateBudgetFilter = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };
  
  // Update completion percentage range filters
  const updateCompletionFilter = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };
  
  // Clear all filters
  const clearAllFilters = () => {
    setFilters({
      status: [],
      budgetWarnings: [],
      searchText: '',
      minBudget: '',
      maxBudget: '',
      minCompletion: '',
      maxCompletion: ''
    });
  };
  
  // Get unique status list for "all statuses" check
  const allStatuses = useMemo(() => {
    return Array.from(new Set(scopes.map(scope => scope.status).filter(Boolean)));
  }, [scopes]);
  
  // Check if all statuses are selected (special default case)
  const allStatusesSelected = useMemo(() => {
    return allStatuses.length > 0 && 
           allStatuses.length === filters.status.length && 
           allStatuses.every(status => filters.status.includes(status));
  }, [allStatuses, filters.status]);
  
  // Check if any filters are active (treat all statuses checked as no filter)
  const hasActiveFilters = useMemo(() => {
    return (
      (filters.status.length > 0 && !allStatusesSelected) ||
      filters.budgetWarnings.length > 0 ||
      filters.searchText.trim() !== '' ||
      filters.minBudget !== '' ||
      filters.maxBudget !== '' ||
      filters.minCompletion !== '' ||
      filters.maxCompletion !== ''
    );
  }, [filters, allStatusesSelected]);
  
  // Apply filtering to scopes
  const filteredScopes = useMemo(() => {
    return scopes.filter(scope => {
      // Status filter
      if (filters.status.length > 0 && !filters.status.includes(scope.status)) {
        return false;
      }
      
      // Budget warnings filters
      if (filters.budgetWarnings.length > 0) {
        // Calculate values needed for warnings
        const remaining = scope.budget 
          ? scope.budget - scope.payments.reduce((sum, payment) => sum + payment.amount, 0) 
          : null;
        const totalContracted = scope.contracts.reduce((sum, contract) => sum + (contract.amount || 0), 0);
        const totalPaid = scope.payments.reduce((sum, payment) => sum + payment.amount, 0);
        
        // Get warning flags
        const isOutOfBudget = remaining !== null && remaining < 0;
        const isContractAmountHigherThanBudget = scope.budget && totalContracted > scope.budget;
        const isPaymentsMoreThanContracted = totalContracted > 0 && totalPaid > totalContracted;
        const hasNoContracts = scope.contracts.length === 0 && scope.budget && scope.budget > 0;
        
        // Check if scope has at least one of the selected warnings
        const hasSelectedWarning = (
          (filters.budgetWarnings.includes('outOfBudget') && isOutOfBudget) ||
          (filters.budgetWarnings.includes('contractsHigher') && isContractAmountHigherThanBudget) ||
          (filters.budgetWarnings.includes('paymentsHigher') && isPaymentsMoreThanContracted) ||
          (filters.budgetWarnings.includes('noContracts') && hasNoContracts)
        );
        
        if (!hasSelectedWarning) {
          return false;
        }
      }
      
      // Text search filter
      if (filters.searchText.trim() !== '') {
        const searchLower = filters.searchText.trim().toLowerCase();
        const scopeMatches = scope.scope?.toLowerCase().includes(searchLower);
        const subcontractorMatches = scope.subcontractor?.toLowerCase().includes(searchLower);
        if (!scopeMatches && !subcontractorMatches) {
          return false;
        }
      }
      
      // Budget range filter
      if (filters.minBudget !== '' && scope.budget < Number(filters.minBudget)) {
        return false;
      }
      if (filters.maxBudget !== '' && scope.budget > Number(filters.maxBudget)) {
        return false;
      }
      
      // Completion percentage range filter
      if (filters.minCompletion !== '' && scope.percentage_complete < Number(filters.minCompletion)) {
        return false;
      }
      if (filters.maxCompletion !== '' && scope.percentage_complete > Number(filters.maxCompletion)) {
        return false;
      }
      
      return true;
    });
  }, [scopes, filters]);
  
  // Apply sorting to filtered data
  const sortedScopes = useMemo(() => {
    let sortableScopes = [...filteredScopes];
    if (sortConfig.key) {
      sortableScopes.sort((a, b) => {
        // Handle custom sortable fields that need calculation
        if (sortConfig.key === 'contracts_total') {
          const aContractsTotal = a.contracts.reduce((sum, contract) => sum + (contract.amount || 0), 0);
          const bContractsTotal = b.contracts.reduce((sum, contract) => sum + (contract.amount || 0), 0);
          return sortConfig.direction === 'ascending'
            ? aContractsTotal - bContractsTotal
            : bContractsTotal - aContractsTotal;
        }
        
        if (sortConfig.key === 'payments_total') {
          const aPaymentsTotal = a.payments.reduce((sum, payment) => sum + payment.amount, 0);
          const bPaymentsTotal = b.payments.reduce((sum, payment) => sum + payment.amount, 0);
          return sortConfig.direction === 'ascending'
            ? aPaymentsTotal - bPaymentsTotal
            : bPaymentsTotal - aPaymentsTotal;
        }
        
        if (sortConfig.key === 'remaining') {
          const aRemaining = a.budget ? a.budget - a.payments.reduce((sum, payment) => sum + payment.amount, 0) : null;
          const bRemaining = b.budget ? b.budget - b.payments.reduce((sum, payment) => sum + payment.amount, 0) : null;
          
          // Handle null values for remaining
          if (aRemaining === null) return sortConfig.direction === 'ascending' ? 1 : -1;
          if (bRemaining === null) return sortConfig.direction === 'ascending' ? -1 : 1;
          
          return sortConfig.direction === 'ascending'
            ? aRemaining - bRemaining
            : bRemaining - aRemaining;
        }
        
        // Handle null values
        if (a[sortConfig.key] === null) return sortConfig.direction === 'ascending' ? 1 : -1;
        if (b[sortConfig.key] === null) return sortConfig.direction === 'ascending' ? -1 : 1;
        
        // Sort strings
        if (typeof a[sortConfig.key] === 'string') {
          return sortConfig.direction === 'ascending'
            ? a[sortConfig.key].localeCompare(b[sortConfig.key])
            : b[sortConfig.key].localeCompare(a[sortConfig.key]);
        }
        
        // Sort numbers
        return sortConfig.direction === 'ascending'
          ? a[sortConfig.key] - b[sortConfig.key]
          : b[sortConfig.key] - a[sortConfig.key];
      });
    }
    return sortableScopes;
  }, [filteredScopes, sortConfig]);
  
  // Group scopes by month based on their timeline_start value
  const groupedScopes = useMemo(() => {
    return groupScopesByMonth(sortedScopes, showMonthHeaders);
  }, [sortedScopes, showMonthHeaders]);
  
  // Handle modal success
  const handleModalSuccess = (result) => {
    // Refresh data to show the new scope
    const loadData = async () => {
      try {
        const data = await fetchConstructionData();
        setScopes(data.scope_items || []);
      } catch (err) {
        console.error(err);
      }
    };
    
    loadData();
  };
  
  if (loading) {
    return <LoadingSkeleton />;
  }
  
  if (error) return <div className="py-4 text-center text-red-500">{error}</div>;
  
  return (
    <div className="py-4">
      <div className="sm:flex sm:items-center mb-6">
        <div className="sm:flex-auto">
          <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Construction Budget Items</h1>
          <p className="mt-2 text-sm text-gray-700 dark:text-gray-300">
            A list of all construction scopes with budget details.
          </p>
        </div>
        <div className="mt-4 sm:ml-16 sm:mt-0 sm:flex-none">
          <button
            type="button"
            onClick={() => setIsModalOpen(true)}
            className="block rounded-md bg-indigo-600 px-3 py-2 text-center text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
          >
            <span className="flex items-center">
              <PlusIcon className="h-5 w-5 mr-1" aria-hidden="true" />
              Add New Scope
            </span>
          </button>
        </div>
      </div>
      
      {/* Filter panel */}
      <FilterPanel 
        showFilterOptions={showFilterOptions}
        statusOptions={statusOptions}
        budgetWarningOptions={budgetWarningOptions}
        filters={filters}
        toggleStatusFilter={toggleStatusFilter}
        setStatusFilterOnly={setStatusFilterOnly}
        toggleBudgetWarningFilter={toggleBudgetWarningFilter}
        setBudgetWarningFilterOnly={setBudgetWarningFilterOnly}
        updateBudgetFilter={updateBudgetFilter}
        updateCompletionFilter={updateCompletionFilter}
        showMonthHeaders={showMonthHeaders}
        setShowMonthHeaders={setShowMonthHeaders}
        clearAllFilters={clearAllFilters}
        setShowFilterOptions={setShowFilterOptions}
      />
      
      {/* New Scope Modal */}
      <NewScopeModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSuccess={handleModalSuccess}
      />
      
      {/* Search bar with filter button */}
      <SearchBar 
        showFilterOptions={showFilterOptions}
        setShowFilterOptions={setShowFilterOptions}
        hasActiveFilters={hasActiveFilters}
        filters={filters}
        updateSearchText={updateSearchText}
      />
      
      {/* Filter chips */}
      <FilterChips 
        hasActiveFilters={hasActiveFilters}
        sortedScopes={sortedScopes}
        scopes={scopes}
        filters={filters}
        budgetWarningOptions={budgetWarningOptions}
        toggleStatusFilter={toggleStatusFilter}
        toggleBudgetWarningFilter={toggleBudgetWarningFilter}
        setFilters={setFilters}
        clearAllFilters={clearAllFilters}
      />
      
      {/* Budget items table */}
      <BudgetItemsTable 
        sortConfig={sortConfig}
        requestSort={requestSort}
        groupedScopes={groupedScopes}
        sortedScopes={sortedScopes}
        hasActiveFilters={hasActiveFilters}
        onSelectScope={onSelectScope}
      />
    </div>
  );
};

export default BudgetItems;